import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface TabItem {
  value: string
  label: ReactNode
}

/**
 * A quiet underline tab bar: the active tab carries an accent underline and
 * full-strength text, the rest stay muted. Controlled via `value` /
 * `onValueChange`. For switching in-page views, not routing.
 */
export function Tabs({
  items,
  value,
  onValueChange,
  className,
}: {
  items: TabItem[]
  value: string
  onValueChange: (value: string) => void
  className?: string
}) {
  return (
    <div role="tablist" className={cn('flex items-end gap-[22px] border-b border-line', className)}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={value === item.value}
          onClick={() => onValueChange(item.value)}
          className={cn(
            '-mb-px inline-flex items-center gap-2 border-b-2 px-px pb-2.5 text-sm font-medium outline-none transition-colors focus-visible:rounded-sm focus-visible:focus-ds',
            value === item.value
              ? 'border-primary text-ink'
              : 'border-transparent text-ink-muted hover:text-ink',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}
